import { Helmet } from "react-helmet-async";
import { Phone, MessageCircle, ArrowLeft, MapPin, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";

const WHATSAPP_URL = "https://wa.me/919891315777?text=Hi%2C%20I%20want%20to%20book%20a%20water%20tank%20cleaning%20service%20in%20Gurugram";

const serviceLinks = [
  { href: "/residential-tank-cleaning-gurugram", label: "Residential Tank Cleaning" },
  { href: "/overhead-water-tank-cleaning-gurugram", label: "Overhead Tank Cleaning" },
  { href: "/underground-water-tank-cleaning-gurugram", label: "Underground Tank Cleaning" },
  { href: "/commercial-water-tank-cleaning-gurugram", label: "Commercial Tank Cleaning" },
];

const nearbyAreas = [
  { href: "/water-tank-cleaning-dlf-phase-gurugram", label: "DLF Phase 1-5" },
  { href: "/water-tank-cleaning-sushant-lok-gurugram", label: "Sushant Lok" },
  { href: "/water-tank-cleaning-palam-vihar-gurugram", label: "Palam Vihar" },
  { href: "/water-tank-cleaning-golf-course-road-gurugram", label: "Golf Course Road" },
];

export default function Blog4() {
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Water-Borne Diseases from Dirty Tanks in Gurugram | TankBuddy</title>
        <meta name="description" content="Dirty water tanks cause typhoid, cholera and gastroenteritis in Gurugram. Learn how to protect your family. TankBuddy +91 98913 15777" />
        <link rel="canonical" href="https://www.tankbuddy.in/blog/waterborne-diseases-dirty-tanks-gurugram" />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Header />

      <main className="pt-20 pb-16">
        <section className="max-w-4xl mx-auto px-4 md:px-8 py-12">

          <a href="/" className="inline-flex items-center gap-2 text-sky text-sm font-medium mb-8 hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </a>

          <div className="inline-flex items-center gap-2 bg-sky/10 text-sky text-sm font-medium px-3 py-1.5 rounded-full mb-6">
            TankBuddy Blog
          </div>

          <h1 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-navy leading-tight mb-6">
            Water-Borne Diseases from Dirty Tanks in Gurugram
          </h1>

          <div className="prose prose-slate max-w-none mb-10">
            <p className="text-slate-600 leading-relaxed text-base">
              Gurugram reports thousands of waterborne disease cases every year. The number one cause? Dirty, uncleaned water storage tanks in homes and apartments. Most families do not realise their tank is the source of repeated illness until it is too late.
            </p>
            <p className="text-slate-600 leading-relaxed text-base mt-4">
              Gurugram hot climate accelerates bacterial growth inside tanks - especially during summer when temperatures exceed 45 degrees. Regular professional cleaning is the only reliable way to protect your family.
            </p>
          </div>

          <div className="bg-navy rounded-xl p-8 mb-10 text-white">
            <h2 className="font-heading font-bold text-xl mb-2">Protect Your Family - Book Today</h2>
            <p className="text-slate-300 text-sm mb-5">Same-day service available. Starting Rs. 999 for residential tanks.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild className="bg-whatsapp hover:bg-whatsapp-hover text-white rounded-full px-6">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-4 h-4 mr-2" /> Book on WhatsApp
                </a>
              </Button>
              <Button asChild variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-6">
                <a href="tel:+919891315777">
                  <Phone className="w-4 h-4 mr-2" /> Call +91 98913 15777
                </a>
              </Button>
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-6">
              Most Common Waterborne Diseases in Gurugram
            </h2>
            <div className="space-y-5">
              {[
                {num:"01", title:"Typhoid Fever", desc:"Caused by Salmonella Typhi bacteria that thrive in contaminated water tanks. Symptoms include high fever, weakness and stomach pain. Gurugram sees peak typhoid cases during summer when tank bacteria multiply fastest."},
                {num:"02", title:"Cholera", desc:"Caused by Vibrio cholerae bacteria in contaminated water. Can cause severe dehydration within hours. Particularly dangerous for children and elderly residents."},
                {num:"03", title:"Gastroenteritis", desc:"The most common waterborne illness in Gurugram. Causes vomiting, diarrhoea and stomach cramps. Often mistaken for food poisoning but frequently caused by contaminated tank water."},
                {num:"04", title:"Hepatitis A", desc:"A viral liver infection spread through contaminated water. Can cause jaundice, fatigue and liver damage. Completely preventable by keeping water tanks clean and sanitized."},
                {num:"05", title:"Skin Infections", desc:"Bathing in contaminated tank water causes rashes, itching and fungal infections. Common in Gurugram humid monsoon season when bacterial growth peaks."},
              ].map((item) => (
                <div key={item.num} className="bg-surface border border-slate-200 rounded-lg p-5">
                  <span className="text-sky font-bold text-sm">Disease {item.num}</span>
                  <h3 className="font-heading font-bold text-navy mt-1 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              Who Is Most at Risk?
            </h2>
            <ul className="space-y-3">
              {[
                "Children under 5 years - immune systems not fully developed",
                "Elderly people above 60 - reduced immunity and resilience",
                "Pregnant women - contamination affects both mother and baby",
                "People with existing health conditions or weak immunity"
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-slate-600">
                  <div className="w-5 h-5 rounded-full bg-sky/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-sky" />
                  </div>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-2xl text-navy mb-4">
              How to Protect Your Family
            </h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                {step:"01", title:"Clean Every 6 Months", desc:"Minimum frequency recommended by WHO for all water storage tanks"},
                {step:"02", title:"Use Certified Technicians", desc:"DIY cleaning misses bacteria in corners and biofilm on walls"},
                {step:"03", title:"Anti-Bacterial Treatment", desc:"Food-grade disinfectants eliminate 99.99% of harmful bacteria"},
                {step:"04", title:"Seal Tank Properly", desc:"Keep lid tightly closed to prevent external contamination"},
              ].map((item) => (
                <div key={item.step} className="bg-surface border border-slate-200 rounded-lg p-5">
                  <span className="text-sky font-bold text-sm">Step {item.step}</span>
                  <h3 className="font-heading font-bold text-navy mt-1 mb-1">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading font-bold text-xl text-navy mb-4">Our Services in Gurugram</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {serviceLinks.map(link => (
                <a key={link.href} href={link.href} className="flex items-center gap-3 bg-surface border border-slate-200 rounded-lg p-4 hover:border-sky/50 hover:shadow-sm transition-all">
                  <div className="w-8 h-8 rounded-lg bg-sky/10 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-4 h-4 text-sky" />
                  </div>
                  <span className="text-sm font-medium text-navy">{link.label}</span>
                </a>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading font-bold text-xl text-navy mb-4">Also Serving Nearby Areas</h2>
            <div className="flex flex-wrap gap-2">
              {nearbyAreas.map(a => (
                <a key={a.href} href={a.href} className="inline-flex items-center gap-1 bg-surface border border-slate-200 text-slate-700 text-xs font-medium px-3 py-1.5 rounded-full hover:border-sky/50 hover:text-navy transition-colors">
                  <MapPin className="w-3 h-3 text-sky" />{a.label}
                </a>
              ))}
            </div>
          </div>

        </section>
      </main>

      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
